/**
 * Rules data hook for the Accounting Rules page.
 *
 * Returns the list of rules for the given tab (bank or integration)
 * in the QueryResult<T> shape used across Novala (data, loading,
 * error, refetch). Also exposes mutation helpers (updateRule,
 * deleteRule, reorderRule) so components can patch the list
 * optimistically.
 *
 * Backend swap: replace the useState initializer with a fetch (or
 * TanStack Query useQuery), replace the mutation function bodies
 * with POST/PATCH/DELETE calls. Nothing in the UI changes.
 *
 * Scope note: in PR 9 the state lives inside this hook, so
 * multiple components calling useRules get independent copies.
 * The Rules page composes a single tree from the top (index.tsx
 * calls useRules once and passes data + mutations down), so this
 * is intentional and matches how Chart of accounts and Bank
 * transactions already work. A shared RulesContext can be added
 * in a follow-up PR if needed.
 */

import { useCallback, useEffect, useState } from "react";
import type { Rule, RuleTab } from "../pages/Accounting/Rules/rulesTypes";
import {
  MOCK_BANK_RULES,
  MOCK_INTEGRATION_RULES,
} from "../pages/Accounting/Rules/rulesMockData";

/** Standard Novala query-result shape (TanStack Query compatible). */
export interface QueryResult<T> {
  data: T;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Return shape adds mutation helpers on top of QueryResult. */
export interface UseRulesResult extends QueryResult<Rule[]> {
  updateRule: (ruleId: string, patch: Partial<Rule>) => void;
  deleteRule: (ruleId: string) => void;
  reorderRule: (ruleId: string, newPriority: number) => void;
}

/**
 * Simulated network latency so the loading state is visible in
 * dev. Set to 0 to disable. Backend swap replaces this entirely
 * with real request timing.
 */
const SIMULATED_LATENCY_MS = 80;

function getMockRulesForTab(tab: RuleTab): Rule[] {
  return tab === "bank" ? MOCK_BANK_RULES : MOCK_INTEGRATION_RULES;
}

export function useRules(tab: RuleTab): UseRulesResult {
  const [data, setData] = useState<Rule[]>(() => getMockRulesForTab(tab));
  const [loading, setLoading] = useState(false);
  const [error] = useState<Error | null>(null);

  // Reload data when the active tab changes.
  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => {
      setData(getMockRulesForTab(tab));
      setLoading(false);
    }, SIMULATED_LATENCY_MS);
    return () => window.clearTimeout(timer);
  }, [tab]);

  const refetch = useCallback(() => {
    setLoading(true);
    window.setTimeout(() => {
      setData(getMockRulesForTab(tab));
      setLoading(false);
    }, SIMULATED_LATENCY_MS);
  }, [tab]);

  /**
   * Patch a rule in place. Used for Status toggle, Auto-post toggle,
   * and Edit-flow updates.
   */
  const updateRule = useCallback(
    (ruleId: string, patch: Partial<Rule>) => {
      setData((prev) =>
        prev.map((r) =>
          r.id === ruleId
            ? { ...r, ...patch, updatedAt: new Date().toISOString() }
            : r,
        ),
      );
    },
    [],
  );

  /**
   * Remove a rule from the list. Called after the Delete
   * confirmation modal is confirmed.
   */
  const deleteRule = useCallback((ruleId: string) => {
    setData((prev) => prev.filter((r) => r.id !== ruleId));
  }, []);

  /**
   * Change a rule's priority. Called by Move up / Move down menu
   * actions and (future) drag-to-reorder. Other rules in the
   * affected range get their priority bumped by 1 in the right
   * direction so sequence stays contiguous.
   */
  const reorderRule = useCallback(
    (ruleId: string, newPriority: number) => {
      setData((prev) => {
        const target = prev.find((r) => r.id === ruleId);
        if (!target) return prev;
        const oldPriority = target.priority;
        if (oldPriority === newPriority) return prev;

        const movingDown = newPriority > oldPriority;

        return prev
          .map((r) => {
            if (r.id === ruleId) return { ...r, priority: newPriority };
            if (movingDown) {
              if (r.priority > oldPriority && r.priority <= newPriority) {
                return { ...r, priority: r.priority - 1 };
              }
            } else {
              if (r.priority >= newPriority && r.priority < oldPriority) {
                return { ...r, priority: r.priority + 1 };
              }
            }
            return r;
          })
          .sort((a, b) => a.priority - b.priority);
      });
    },
    [],
  );

  return {
    data,
    loading,
    error,
    refetch,
    updateRule,
    deleteRule,
    reorderRule,
  };
}